import type { PropsWithChildren, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { SrOrderLineStatus, SrSalesOrderType } from '@plato/data/starrocks/types';
import { Badge } from '@plato/ui-legacy/components/list-views/badge';

type Props = {
    status: SrOrderLineStatus;
    type: SrSalesOrderType;
    className?: string;
    children?: ReactNode;
} & PropsWithChildren;

export function LineItemStatusBadge({ status, type, children, className }: Props) {
    const { t } = useTranslation('order-status');
    const getStatusLabel = (status: SrOrderLineStatus): string => {
        switch (status) {
            case SrOrderLineStatus.OPEN:
                return t('OPEN');
            case SrOrderLineStatus.OPEN_IN_PICKING:
                return t('OPEN_IN_PICKING');
            case SrOrderLineStatus.RELEASED_FOR_DELIVERY:
                return t('RELEASED_FOR_DELIVERY');
            case SrOrderLineStatus.RELEASED_FOR_BILLING:
                return t('RELEASED_FOR_BILLING');
            case SrOrderLineStatus.BILLED:
                return t('BILLED');
            case SrOrderLineStatus.PROCESSED_INTERNALLY:
                return t('PROCESSED_INTERNALLY');
            case SrOrderLineStatus.CANCELLED:
                return t('CANCELLED');
            case SrOrderLineStatus.UNKNOWN:
                return t('UNKNOWN');
            case SrOrderLineStatus.RETURNED:
                return t('RETURNED');
            case SrOrderLineStatus.SHIPPED:
                return t('SHIPPED');
            case SrOrderLineStatus.PARTIALLY_BILLED:
                return t('PARTIALLY_BILLED');
            case SrOrderLineStatus.PARTIALLY_SHIPPED:
                return t('PARTIALLY_SHIPPED');
            case SrOrderLineStatus.PARTIALLY_RECEIVED:
                return t('PARTIALLY_RECEIVED');
            case SrOrderLineStatus.PENDING:
                return t('PENDING');
            case SrOrderLineStatus.RECEIVED:
                return t('RECEIVED');
            case SrOrderLineStatus.LOST:
                return t('LOST');
            case SrOrderLineStatus.OTHER:
                return t('OTHER');
            case SrOrderLineStatus.WON:
                return t('WON');
        }
    };
    const colour = getOrderLineStatusColor(type, status);

    return (
        <Badge color={colour} className={className}>
            {children ?? getStatusLabel(status)}
        </Badge>
    );
}

type StatusColor = 'yellow' | 'orange' | 'green' | 'red' | 'blue' | 'neutral';

function getOrderLineStatusColor(type: SrSalesOrderType, status: SrOrderLineStatus): StatusColor {
    if (type === SrSalesOrderType.QUOTE) {
        switch (status) {
            case SrOrderLineStatus.LOST:
                return 'red';
            case SrOrderLineStatus.OPEN:
                return 'yellow';
            case SrOrderLineStatus.PARTIALLY_RECEIVED:
                return 'blue';
            case SrOrderLineStatus.RECEIVED:
                return 'green';
            default:
                return 'neutral';
        }
    }
    switch (status) {
        case SrOrderLineStatus.BILLED:
        case SrOrderLineStatus.PROCESSED_INTERNALLY:
            return 'green';
        case SrOrderLineStatus.OPEN:
            return 'red';
        case SrOrderLineStatus.RELEASED_FOR_BILLING:
            return 'blue';
        default:
            return 'neutral';
    }
}
